import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import Loader from 'components/loader/Loader';
import { describeChange } from './auditValues';

const CELL_BORDER = { '&:last-child td, &:last-child th': { border: 0 } };
const LABEL_CELL = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  fontWeight: 500,
  verticalAlign: 'top',
};
const VALUE_CELL = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  fontWeight: 500,
  verticalAlign: 'top',
};
// The heading row for an object-valued field: it names the field and carries
// no values of its own, so it loses the rule beneath it and sits tight against
// the figures it introduces.
const GROUP_ROW = { '& td, & th': { borderBottom: 0, pb: 0 } };
// Its figures are indented under it, and named in lighter type: the bold one
// above is the field a reader is scanning for, these are its parts.
const SUB_LABEL_CELL = {
  ...LABEL_CELL,
  pl: 3,
  fontWeight: 400,
  color: 'text.secondary',
};

// Duty type is stored as an integer. The master list is what the duty slip
// modal and the reservation form already name it by, so the audit trail says
// the same thing they do; auditValues falls back to a constant map when the
// list has not loaded on this page.
const useDutyTypeName = () => {
  const masterDutyTypeList = useSelector((state) => state?.userDutyType?.dutyTypeList?.data);
  return React.useCallback(
    (value) =>
      (Array.isArray(masterDutyTypeList) &&
        masterDutyTypeList.find((d) => Number(d?.dutyTypeId) === Number(value))?.name) ||
      null,
    [masterDutyTypeList],
  );
};

const AuditTrailDetails = ({ data, loading }) => {
  const dutyTypeName = useDutyTypeName();

  if (loading) {
    return (
      <Box
        sx={{
          width: '95vw',
          maxWidth: 1100,
          maxHeight: '85vh',
          overflowY: 'auto',
          p: 3,
        }}
      >
        <Loader height={'50vh'} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '95vw',
        maxWidth: 1100,
        maxHeight: '85vh',
        overflowY: 'auto',
        p: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          Audit Trail Details
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Entity
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data?.entityLabel || '-'}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Action
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data?.actionLabel || '-'}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Performed By
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data?.performedByName || '-'}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Role
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data?.performedByRole || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Company
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {/* Some company names carry stray whitespace from data entry — one
                on this box begins with a literal tab, which indents the cell
                wherever the name is rendered pre-wrap. Trimmed on display; the
                stored value is a data fix, not a display one. */}
            {String(data?.companyName || '').trim() || 'Global'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Date
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {data?.createdAt
              ? new Date(data.createdAt)
                  .toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })
                  .replace(',', '')
              : '-'}
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      {/* Values Changed Section */}
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Values Changed ({data?.valuesChanged?.length || 0})
      </Typography>

      {data?.valuesChanged?.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'visible' }}>
          <Table stickyHeader aria-label="changes table" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 150 }} align="left">
                  Old Value
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 150 }} align="left">
                  New Value
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.valuesChanged.map((item, index) => {
                const change = describeChange(item, { dutyTypeName });

                // A rate card is a whole object. Printing it as one value said
                // "[object Object]"; what a reader wants is the figures that
                // moved inside it, one per line under the field's own name.
                // An object-valued field becomes one heading row naming the
                // field, then a row per figure that moved inside it. They are
                // real table rows rather than a grid nested in one cell, so
                // every old and new value sits under the column header that
                // names it — which is also why no arrow is needed to say which
                // way the change runs.
                if (change.rows) {
                  if (change.empty) {
                    return (
                      <TableRow key={index} sx={CELL_BORDER}>
                        <TableCell sx={LABEL_CELL} component="th" scope="row">
                          {change.label}
                        </TableCell>
                        <TableCell colSpan={2} sx={VALUE_CELL}>
                          <Typography variant="body2" color="text.secondary">
                            Replaced, with no change to the figures
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <React.Fragment key={index}>
                      <TableRow sx={GROUP_ROW}>
                        <TableCell sx={LABEL_CELL} component="th" scope="row">
                          {change.label}
                        </TableCell>
                        <TableCell sx={VALUE_CELL} />
                        <TableCell sx={VALUE_CELL} />
                      </TableRow>
                      {change.rows.map((row) => (
                        <TableRow key={`${index}-${row.path}`} sx={CELL_BORDER}>
                          <TableCell sx={SUB_LABEL_CELL} component="th" scope="row">
                            {row.label}
                          </TableCell>
                          <TableCell sx={VALUE_CELL} align="left">
                            {row.oldValue}
                          </TableCell>
                          <TableCell
                            sx={{ ...VALUE_CELL, color: 'green', fontWeight: 500 }}
                            align="left"
                          >
                            {row.newValue}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                }

                return (
                  <TableRow key={index} sx={CELL_BORDER}>
                    <TableCell sx={LABEL_CELL} component="th" scope="row">
                      {change.label}
                    </TableCell>
                    <TableCell sx={VALUE_CELL} align="left">
                      {change.oldValue}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        ...VALUE_CELL,
                        // An unchanged value is still recorded, and hiding it
                        // would be the wrong instinct in an audit trail — but
                        // it has not moved, so it does not get the green that
                        // means "this is the new value".
                        color: change.unchanged ? 'text.secondary' : 'green',
                        fontWeight: 500,
                      }}
                    >
                      {change.newValue}
                      {change.unchanged ? ' (unchanged)' : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary">No Changes Found</Typography>
      )}
    </Box>
  );
};

export default AuditTrailDetails;
